/* MP CART DRAWER — <cart-drawer> element for sections/mp-cart-drawer.liquid.
   Replaces stock cart-drawer.js (swapped in sections/header.liquid — see the
   / +++ / MP / +++ / marker). Implements the same public contract that
   product-form.js and mp-addon-upsell.js rely on after add-to-cart:
     - document.querySelector('cart-drawer')
     - getSectionsToRender().map(s => s.id)  → section names for /cart/add
     - renderContents(parsedState)           → swap + open
     - open() / close() / setActiveElement()
   Everything INSIDE the drawer (quantity, remove, note, discount codes,
   cross-sell, count chip) is driven by stock cart.js + mp-cart.js, because
   the drawer reuses the cart page's element ids and region classes. */

(() => {
	// Diagnostic version stamp — bump when editing this file.
	window.__mpVersions = Object.assign({}, window.__mpVersions, { 'mp-cart-drawer.js': 'v17' })
	console.info('[MP cart] mp-cart-drawer.js v17 loaded')

	if (customElements.get('cart-drawer')) return

	class MPCartDrawer extends HTMLElement {
		constructor() {
			super()

			this.header = document.querySelector('.header-wrapper')
			if (this.header) this.header.preventHide = false

			this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close())
			this.bindOverlay()
			this.setHeaderCartIconAccessibility()
		}

		bindOverlay() {
			const overlay = this.querySelector('#CartDrawer-Overlay')
			if (overlay) overlay.addEventListener('click', this.close.bind(this))
		}

		setHeaderCartIconAccessibility() {
			const cartLink = document.querySelector('#cart-icon-bubble')
			if (!cartLink) return
			cartLink.setAttribute('role', 'button')
			cartLink.setAttribute('aria-haspopup', 'dialog')
			cartLink.addEventListener('click', (event) => {
				event.preventDefault()
				this.open(cartLink)
			})
			cartLink.addEventListener('keydown', (event) => {
				if (event.code.toUpperCase() === 'SPACE') {
					event.preventDefault()
					this.open(cartLink)
				}
			})
		}

		open(triggeredBy) {
			// Self-diagnosis: versions + the layout numbers that distinguish the
			// intermittent "doubled padding" bug (healthy reference values:
			// htmlFont 10px, headerPad "20px 20px 14px", title 22px, panels 1,
			// pageClasses 0). Screenshot this console line when it looks broken.
			const cssVersion = getComputedStyle(this).getPropertyValue('--mp-css-v').trim()
			if (cssVersion !== 'v17') {
				console.warn(
					`[MP cart] DRAWER CSS PROBLEM: expected v17, got "${cssVersion || 'NOTHING (stylesheet missing/stale)'}" — screenshot this console + the URL bar and send it to Claude.`
				)
			}
			const headerEl = this.querySelector('.mp-drawer__header')
			const titleEl = this.querySelector('.mp-drawer__title')
			const diag = {
				versions: window.__mpVersions,
				css: cssVersion || 'MISSING',
				htmlFont: getComputedStyle(document.documentElement).fontSize,
				headerPad: headerEl ? getComputedStyle(headerEl).padding : 'NO HEADER',
				title: titleEl ? getComputedStyle(titleEl).fontSize : 'NO TITLE',
				panels: this.querySelectorAll('.mp-drawer').length,
				pageClasses: this.querySelectorAll('.mp-cart__header, .mp-cart__layout, .mp-cart__container').length,
				dpr: window.devicePixelRatio,
				vw: window.innerWidth,
			}
			console.info('[MP cart] drawer open diag:', JSON.stringify(diag))

			if (this.header) this.header.preventHide = true
			if (triggeredBy) this.setActiveElement(triggeredBy)

			// The animation doesn't always trigger without a frame's delay.
			setTimeout(() => {
				this.classList.add('animate', 'active')
			})

			this.addEventListener(
				'transitionend',
				() => {
					const container = document.getElementById('CartDrawer') || this
					const focusElement = this.querySelector('.mp-drawer') || this.querySelector('.mp-drawer__close')
					trapFocus(container, focusElement)
				},
				{ once: true }
			)

			document.body.classList.add('overflow-hidden-drawer')
		}

		close() {
			this.classList.remove('active')
			removeTrapFocus(this.activeElement)
			document.body.classList.remove('overflow-hidden-drawer')
			if (this.header) this.header.preventHide = false
		}

		setActiveElement(element) {
			this.activeElement = element
		}

		/* product-form.js maps .id as the section names for the /cart/add
		   sections bundle, then feeds the response back to renderContents. */
		getSectionsToRender() {
			return [
				{
					id: this.dataset.sectionId || 'mp-cart-drawer',
					selector: '#CartDrawer',
				},
				{
					id: 'cart-icon-bubble',
				},
			]
		}

		renderContents(parsedState) {
			this.classList.remove('is-empty')
			const sections = parsedState.sections || {}
			const drawerSectionId = this.dataset.sectionId || 'mp-cart-drawer'

			// Drawer body: swap #CartDrawer's contents wholesale.
			if (sections[drawerSectionId]) {
				const target = this.querySelector('#CartDrawer')
				const source = new DOMParser()
					.parseFromString(sections[drawerSectionId], 'text/html')
					.querySelector('#CartDrawer')
				if (target && source) target.innerHTML = source.innerHTML
			}

			/* Header bubble: #cart-icon-bubble holds icon + label markup that
			   the section render does not include — never replace its
			   contents; surgically update only the count (helper from
			   mp-cart.js, loaded before this file). */
			if (sections['cart-icon-bubble'] && window.mpUpdateCartBubble) {
				window.mpUpdateCartBubble(sections['cart-icon-bubble'])
			}

			setTimeout(() => {
				this.bindOverlay()
				this.open()
			})
		}
	}

	customElements.define('cart-drawer', MPCartDrawer)

	/* Redundant safety: the same CartItems prototype protection mp-cart.js
	   applies. Stock onCartUpdate — triggered by product-form's cartUpdate
	   publish on EVERY add — fetches the stock main-cart-items section into
	   whatever cart-items element hears it, clobbering the drawer with
	   stock-classed markup. Duplicated here (guarded) so a stale-cached
	   mp-cart.js can never reopen that hole. */
	const guardCartItems = () => {
		const CartItemsClass = customElements.get('cart-items')
		if (!CartItemsClass || CartItemsClass.prototype.__mpPatched) return
		CartItemsClass.prototype.__mpPatched = true
		CartItemsClass.prototype.onCartUpdate = function () {
			const id = this.dataset.source || document.getElementById('main-cart-items')?.dataset.id
			if (!id) return Promise.resolve()
			return fetch(`${window.location.pathname}?section_id=${id}`)
				.then((response) => response.text())
				.then((html) => {
					const doc = new DOMParser().parseFromString(html, 'text/html')
					const swap = (selector) => {
						const target = document.querySelector(selector)
						const source = doc.querySelector(selector)
						if (target && source) target.innerHTML = source.innerHTML
					}
					swap('.js-contents')
					swap('.js-contents-totals')
					swap('.js-contents-shipping')
					swap('.js-contents-upsell')
				})
				.catch((e) => {
					console.error(e)
				})
		}
		const originalGetSections = CartItemsClass.prototype.getSectionsToRender
		CartItemsClass.prototype.getSectionsToRender = function () {
			return originalGetSections.call(this).filter((section) => section.id !== 'cart-icon-bubble')
		}
	}
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', guardCartItems)
	} else {
		guardCartItems()
	}
})()
